import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Loader2, Check, FileText, Download, Plus, Trash2, Save, GripVertical } from 'lucide-react';
import { eventsAPI } from '../lib/api';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import html2pdf from 'html2pdf.js';
import { AVF_LOGO_BASE64 } from '../lib/avfLogo';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const SortableEquipmentRow = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1 : 0,
    position: 'relative',
  };

  return (
    <div ref={setNodeRef} style={style} className={`flex gap-2 items-start ${isDragging ? 'bg-muted/50 rounded' : ''}`}>
      <button
        type="button"
        className="cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded touch-none mt-1.5"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4 text-muted-foreground" />
      </button>
      {children}
    </div>
  );
};

const LogisticsLetterTab = ({ event, contacts, allContacts, location, onUpdate }) => {
  // Get venue contacts - prioritize contacts linked to this event with "venue" role_tags,
  // but also include any contact with "venue" role_tags from the full contacts list
  const venueContacts = React.useMemo(() => {
    const eventContactIds = (contacts || []).map(c => c.contact_id).filter(Boolean);
    
    // Filter allContacts to find those with "venue" role tag
    const venueFromAll = (allContacts || []).filter(c => 
      c.role_tags && c.role_tags.includes('venue')
    );
    
    // Prioritize venue contacts that are also linked to this event
    const linkedVenue = venueFromAll.filter(c => eventContactIds.includes(c.id));
    const otherVenue = venueFromAll.filter(c => !eventContactIds.includes(c.id));
    
    return [...linkedVenue, ...otherVenue];
  }, [contacts, allContacts]);

  // Initialize logistics letter data from event or defaults
  const [letterData, setLetterData] = useState(() => {
    const saved = event?.logistics_letter || {};
    return {
      recipient_contact_id: saved.recipient_contact_id || '',
      letter_date: saved.letter_date || format(new Date(), 'yyyy-MM-dd'),
      cc_contacts: saved.cc_contacts || [],
      // Editable sections
      intro_text: saved.intro_text || getDefaultIntro(event, location),
      room_availability: saved.room_availability || getDefaultRoomAvailability(event, location),
      loading_dock: saved.loading_dock || getDefaultLoadingDock(event, location),
      pre_load_in: saved.pre_load_in || 'Please make sure the Ballroom is clear of all tables, chairs, and stage deck prior to our load in.',
      schedule_notes: saved.schedule_notes || '',
      electrical_text: saved.electrical_text || getDefaultElectricalText(event),
      rigging_text: saved.rigging_text || 'All equipment for this event will be ground supported.',
      venue_equipment: saved.venue_equipment || [],
      case_storage: saved.case_storage || '',
      closing_text: saved.closing_text || 'Thank you in advance for your assistance with this project.',
      sender_name: saved.sender_name || '',
      sender_phone: saved.sender_phone || '',
      sender_email: saved.sender_email || '',
    };
  });

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [exporting, setExporting] = useState(false);
  const pendingChanges = useRef(null);
  const saveTimeoutRef = useRef(null);

  // Helper functions to generate default text
  function getDefaultIntro(event, location) {
    const clientName = event?.client_name || '[Client Name]';
    const eventName = event?.event_name || '[Event Name]';
    const venueName = location?.location_name || '[Venue Name]';
    const room = event?.room || location?.room || '';
    const startDate = event?.start_date ? format(parseISO(event.start_date), 'MMMM d, yyyy') : '[Start Date]';
    const endDate = event?.end_date ? format(parseISO(event.end_date), 'MMMM d, yyyy') : '';
    const dateRange = endDate && endDate !== startDate ? `${startDate} - ${endDate}` : startDate;
    
    return `AV Factory has been contracted by ${clientName} to provide production services and support for the ${eventName} at the ${venueName}${room ? ` in ${room}` : ''} on ${dateRange}.

This letter will review room availability, load in/load out, security, electrical needs, venue supplied equipment, rigging, etc.

This letter does not reflect any other vendor's working logistics on the event.

If there are any questions or concerns with anything detailed within this document please contact me as soon as possible.

A ground plan is attached for your reference.`;
  }

  function getDefaultRoomAvailability(event, location) {
    const room = event?.room || location?.room || '[Room]';
    const startDate = event?.start_date ? format(parseISO(event.start_date), 'EEEE, MMMM d') : '[Start Date]';
    const endDate = event?.end_date ? format(parseISO(event.end_date), 'EEEE, MMMM d') : '';
    
    if (endDate && endDate !== startDate) {
      return `${room} will be available to AVF from ${startDate} through ${endDate}.`;
    }
    return `${room} will be available to AVF on ${startDate}.`;
  }

  function getDefaultLoadingDock(event, location) {
    const dockNotes = location?.dock_notes || '';
    return `Loading dock access will be required for load in and load out.${dockNotes ? `\n\nDock Notes: ${dockNotes}` : ''}`;
  }

  function getDefaultElectricalText(event) {
    const connections = event?.electric?.connections || [];
    if (connections.length === 0) {
      return 'Electrical requirements to be determined.';
    }
    
    const lines = connections.map(conn => {
      const parts = [];
      if (conn.amperage) parts.push(`${conn.amperage} amp`);
      if (conn.phase) parts.push(conn.phase);
      if (conn.connector_type) parts.push(`${conn.connector_type} connections`);
      if (conn.connection_location) parts.push(`at ${conn.connection_location}`);
      return parts.join(' ');
    });
    
    return lines.join('\n') + '\n\nPlease reference the attached ground plan for location.\n\nAny charges associated with power consumption should be billed to the event\'s master account.';
  }

  // Get selected recipient contact details
  const selectedRecipient = venueContacts.find(c => c.id === letterData.recipient_contact_id);

  // Save function
  const doSave = useCallback(async () => {
    if (!pendingChanges.current) return;
    
    setSaving(true);
    try {
      await onUpdate({ logistics_letter: pendingChanges.current });
      pendingChanges.current = null;
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      console.error('Failed to save logistics letter:', error);
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  }, [onUpdate]);

  // Queue a change with debounce
  const queueChange = useCallback((newData) => {
    pendingChanges.current = newData;
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(doSave, 1000);
  }, [doSave]);

  // Save immediately on blur
  const saveNow = useCallback(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    doSave();
  }, [doSave]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  // Handle field changes
  const handleChange = (field, value) => {
    const newData = { ...letterData, [field]: value };
    setLetterData(newData);
    queueChange(newData);
  };

  // Venue equipment management
  const addEquipmentItem = () => {
    const newEquipment = [...letterData.venue_equipment, { id: `eq-${Date.now()}`, qty: '1', description: '' }];
    handleChange('venue_equipment', newEquipment);
  };

  const updateEquipmentItem = (index, field, value) => {
    const newEquipment = [...letterData.venue_equipment];
    newEquipment[index] = { ...newEquipment[index], [field]: value };
    handleChange('venue_equipment', newEquipment);
  };

  const removeEquipmentItem = (index) => {
    const newEquipment = letterData.venue_equipment.filter((_, i) => i !== index);
    handleChange('venue_equipment', newEquipment);
    saveNow();
  };

  const handleEquipmentDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const equipment = letterData.venue_equipment.map((item, i) => ({ ...item, id: item.id || `eq-${i}` }));
    const oldIndex = equipment.findIndex(item => item.id === active.id);
    const newIndex = equipment.findIndex(item => item.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(equipment, oldIndex, newIndex);
    handleChange('venue_equipment', reordered);
    saveNow();
  };

  const equipmentSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // CC contacts management
  const addCCContact = () => {
    const newCC = [...letterData.cc_contacts, { name: '', organization: '' }];
    handleChange('cc_contacts', newCC);
  };

  const updateCCContact = (index, field, value) => {
    const newCC = [...letterData.cc_contacts];
    newCC[index] = { ...newCC[index], [field]: value };
    handleChange('cc_contacts', newCC);
  };

  const removeCCContact = (index) => {
    const newCC = letterData.cc_contacts.filter((_, i) => i !== index);
    handleChange('cc_contacts', newCC);
    saveNow();
  };

  // Generate production schedule from event schedule - only crew call items
  const generateScheduleHTML = () => {
    const schedule = event?.schedule || [];
    const crewCallItems = schedule.filter(item => 
      item.call_sheet === true
    ).sort((a, b) => {
      const dateA = a.date || '';
      const dateB = b.date || '';
      if (dateA !== dateB) return dateA.localeCompare(dateB);
      return (a.time || '').localeCompare(b.time || '');
    });

    if (crewCallItems.length === 0) {
      return '<p>No schedule items available.</p>';
    }

    // Group by date
    const byDate = {};
    crewCallItems.forEach(item => {
      const date = item.date || 'TBD';
      if (!byDate[date]) byDate[date] = [];
      byDate[date].push(item);
    });

    let html = '';
    Object.entries(byDate).forEach(([date, items]) => {
      const formattedDate = date !== 'TBD' ? format(parseISO(date), 'EEEE, MMMM d, yyyy').toUpperCase() : 'TBD';
      html += `<p style="font-weight: bold; margin: 16px 0 8px 0; font-size: 12px;">${formattedDate}</p>`;
      html += '<table style="width: 100%; border-collapse: collapse; font-size: 11px;">';
      items.forEach(item => {
        const time = item.time ? formatTime(item.time) : '';
        const action = item.action || '';
        html += `<tr>
          <td style="padding: 4px 8px 4px 0; width: 80px; vertical-align: top; color: #333;">${time}</td>
          <td style="padding: 4px 0; color: #333;">${action}</td>
        </tr>`;
      });
      html += '</table>';
    });

    return html;
  };

  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const ampm = h >= 12 ? 'pm' : 'am';
    const h12 = h % 12 || 12;
    return `${h12}:${minutes} ${ampm}`;
  };

  // Export to PDF
  const exportPDF = async () => {
    if (!selectedRecipient) {
      toast.error('Please select a venue contact');
      return;
    }

    setExporting(true);
    try {
      const letterDate = letterData.letter_date ? format(parseISO(letterData.letter_date), 'MMMM d, yyyy') : '';
      const eventName = event?.event_name || '';
      const flexNumber = event?.flex_number || '';
      const clientName = event?.client_name || '';
      const startDate = event?.start_date ? format(parseISO(event.start_date), 'MMMM d, yyyy') : '';
      const endDate = event?.end_date ? format(parseISO(event.end_date), 'MMMM d, yyyy') : '';
      const dateRange = endDate && endDate !== startDate ? `${startDate} - ${endDate}` : startDate;

      // Venue equipment HTML
      let equipmentHTML = '';
      if (letterData.venue_equipment.length > 0) {
        equipmentHTML = '<table style="width: 100%; border-collapse: collapse; font-size: 11px;">';
        letterData.venue_equipment.forEach(item => {
          equipmentHTML += `<tr>
            <td style="padding: 4px 8px 4px 0; width: 40px; vertical-align: top;">${item.qty}</td>
            <td style="padding: 4px 0;">${item.description}</td>
          </tr>`;
        });
        equipmentHTML += '</table>';
      } else {
        equipmentHTML = '<p style="color: #666; font-size: 11px;">No venue equipment specified.</p>';
      }

      // CC section
      let ccHTML = '';
      if (letterData.cc_contacts.length > 0) {
        ccHTML = '<p style="font-size: 11px; margin-top: 24px;"><strong>CC:</strong></p>';
        letterData.cc_contacts.forEach(cc => {
          ccHTML += `<p style="font-size: 11px; margin: 2px 0;">${cc.name}${cc.organization ? ` - ${cc.organization}` : ''}</p>`;
        });
      }

      const html = `
        <div style="font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 20px; color: #333;">
          <!-- Header -->
          <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 24px;">
            <div>
              <img src="${AVF_LOGO_BASE64}" alt="AV Factory" style="height: 50px; width: auto;" />
            </div>
            <div style="text-align: right; font-size: 11px; color: #666;">
              ${letterDate}
            </div>
          </div>

          <!-- Recipient -->
          <div style="margin-bottom: 24px; font-size: 11px;">
            <p style="margin: 0; font-weight: bold;">${selectedRecipient.contact_name || selectedRecipient.name}</p>
            ${selectedRecipient.email ? `<p style="margin: 2px 0;">${selectedRecipient.email}</p>` : ''}
            ${location?.location_name ? `<p style="margin: 2px 0;">${location.location_name}</p>` : ''}
            ${location?.address ? `<p style="margin: 2px 0;">${location.address}</p>` : ''}
            ${location?.city || location?.state || location?.zip_code ? 
              `<p style="margin: 2px 0;">${[location.city, location.state, location.zip_code].filter(Boolean).join(' ')}</p>` : ''}
          </div>

          <!-- Subject -->
          <div style="margin-bottom: 24px;">
            <p style="font-size: 12px; margin: 0;">
              <strong>RE:</strong> ${clientName} - ${eventName}${flexNumber ? ` (${flexNumber})` : ''} - ${dateRange}
            </p>
          </div>

          <!-- Salutation & Intro -->
          <div style="margin-bottom: 24px; font-size: 11px; line-height: 1.6;">
            <p style="margin: 0 0 16px 0;">Dear ${(selectedRecipient.contact_name || selectedRecipient.name)?.split(' ')[0] || 'Team'},</p>
            ${letterData.intro_text.split('\n').map(p => `<p style="margin: 0 0 12px 0;">${p}</p>`).join('')}
          </div>

          <!-- Production Schedule -->
          <div style="margin-bottom: 24px;">
            <h2 style="font-size: 14px; font-weight: bold; margin: 0 0 12px 0; border-bottom: 1px solid #ccc; padding-bottom: 4px;">Production Schedule</h2>
            <div style="font-size: 11px; margin-bottom: 8px;">
              <p style="margin: 0 0 8px 0;">${letterData.room_availability}</p>
              <p style="margin: 0 0 8px 0;">${letterData.loading_dock}</p>
              <p style="margin: 0 0 8px 0;">${letterData.pre_load_in}</p>
            </div>
            ${generateScheduleHTML()}
            ${letterData.schedule_notes ? `<p style="font-size: 11px; margin-top: 12px; font-style: italic;">${letterData.schedule_notes}</p>` : ''}
          </div>

          <!-- Electrical Requirements -->
          <div style="margin-bottom: 24px;">
            <h2 style="font-size: 14px; font-weight: bold; margin: 0 0 12px 0; border-bottom: 1px solid #ccc; padding-bottom: 4px;">Electrical Requirements</h2>
            <div style="font-size: 11px; line-height: 1.6;">
              ${letterData.electrical_text.split('\n').map(p => `<p style="margin: 0 0 8px 0;">${p}</p>`).join('')}
            </div>
          </div>

          <!-- Rigging & Lighting -->
          <div style="margin-bottom: 24px;">
            <h2 style="font-size: 14px; font-weight: bold; margin: 0 0 12px 0; border-bottom: 1px solid #ccc; padding-bottom: 4px;">Rigging & Lighting</h2>
            <p style="font-size: 11px;">${letterData.rigging_text}</p>
          </div>

          <!-- Venue Supplied Equipment -->
          <div style="margin-bottom: 24px;">
            <h2 style="font-size: 14px; font-weight: bold; margin: 0 0 12px 0; border-bottom: 1px solid #ccc; padding-bottom: 4px;">Venue Supplied Equipment</h2>
            ${equipmentHTML}
          </div>

          <!-- Empty Case Storage -->
          ${letterData.case_storage ? `
          <div style="margin-bottom: 24px;">
            <h2 style="font-size: 14px; font-weight: bold; margin: 0 0 12px 0; border-bottom: 1px solid #ccc; padding-bottom: 4px;">Empty Case Storage</h2>
            <p style="font-size: 11px;">${letterData.case_storage}</p>
          </div>
          ` : ''}

          <!-- Closing -->
          <div style="margin-bottom: 24px; font-size: 11px;">
            <p>${letterData.closing_text}</p>
          </div>

          <!-- Signature -->
          <div style="margin-bottom: 24px; font-size: 11px;">
            <p style="margin: 0;">${letterData.sender_name || '[Your Name]'}</p>
            <p style="margin: 2px 0;">AV Factory</p>
            ${letterData.sender_phone ? `<p style="margin: 2px 0;">${letterData.sender_phone}</p>` : ''}
            ${letterData.sender_email ? `<p style="margin: 2px 0;">${letterData.sender_email}</p>` : ''}
          </div>

          ${ccHTML}

          <!-- Footer -->
          <div style="margin-top: 48px; padding-top: 12px; border-top: 1px solid #ccc; font-size: 10px; color: #666; text-align: center;">
            <p style="margin: 0;">Audio Visual Factory | 22018 72nd Ave S, Kent WA 98032 | 206-763-7181</p>
          </div>
        </div>
      `;

      const container = document.createElement('div');
      container.innerHTML = html;
      document.body.appendChild(container);

      const filename = `${flexNumber || 'Event'}_Logistics_Letter.pdf`;
      
      const worker = html2pdf()
        .set({
          margin: [0.5, 0.5, 0.5, 0.5],
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' },
          pagebreak: { mode: 'avoid-all' }
        })
        .from(container)
        .toPdf();

      const pdfObj = await worker.get('pdf');
      const pdfBlob = pdfObj.output('blob');
      document.body.removeChild(container);
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success('Logistics letter exported');
    } catch (error) {
      console.error('Failed to export PDF:', error);
      toast.error('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Card className="border-border/50 bg-card/50">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-400" />
            Logistics Letter
          </CardTitle>
          <div className="flex items-center gap-2">
            {saving && (
              <Badge variant="outline" className="border-blue-500/30 bg-blue-500/10 text-blue-400">
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                Saving...
              </Badge>
            )}
            {saved && !saving && (
              <Badge variant="outline" className="border-emerald-500/30 bg-emerald-500/10 text-emerald-400">
                <Check className="h-3 w-3 mr-1" />
                Saved
              </Badge>
            )}
            <Button onClick={exportPDF} disabled={exporting} data-testid="export-logistics-letter-btn">
              {exporting ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Download className="h-4 w-4 mr-2" />
              )}
              Export PDF
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Letter Header Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Letter Date</Label>
            <Input
              type="date"
              value={letterData.letter_date}
              onChange={(e) => handleChange('letter_date', e.target.value)}
              onBlur={saveNow}
              className="bg-background/50"
            />
          </div>
          <div className="space-y-2">
            <Label>Venue Contact (Recipient) *</Label>
            <Select 
              value={letterData.recipient_contact_id} 
              onValueChange={(v) => { handleChange('recipient_contact_id', v); saveNow(); }}
            >
              <SelectTrigger className="bg-background/50" data-testid="venue-contact-select">
                <SelectValue placeholder="Select venue contact" />
              </SelectTrigger>
              <SelectContent>
                {venueContacts.length === 0 ? (
                  <SelectItem value="_none" disabled>No venue contacts found</SelectItem>
                ) : (
                  venueContacts.map(contact => (
                    <SelectItem key={contact.id} value={contact.id}>
                      {contact.contact_name || contact.name} {contact.email ? `(${contact.email})` : ''}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            {venueContacts.length === 0 && (
              <p className="text-xs text-yellow-400">Add contacts with "venue" role tag in the Contacts tab</p>
            )}
          </div>
        </div>

        <Separator />

        {/* Sender Info */}
        <div>
          <Label className="text-sm text-muted-foreground uppercase tracking-wider mb-3 block">Sender Information</Label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Your Name</Label>
              <Input
                value={letterData.sender_name}
                onChange={(e) => handleChange('sender_name', e.target.value)}
                onBlur={saveNow}
                placeholder="Christopher Baldini"
                className="bg-background/50"
              />
            </div>
            <div className="space-y-2">
              <Label>Your Phone</Label>
              <Input
                value={letterData.sender_phone}
                onChange={(e) => handleChange('sender_phone', e.target.value)}
                onBlur={saveNow}
                placeholder="206-790-2632"
                className="bg-background/50"
              />
            </div>
            <div className="space-y-2">
              <Label>Your Email</Label>
              <Input
                value={letterData.sender_email}
                onChange={(e) => handleChange('sender_email', e.target.value)}
                onBlur={saveNow}
                placeholder="cbaldini@avfactory.com"
                className="bg-background/50"
              />
            </div>
          </div>
        </div>

        <Separator />

        {/* Introduction */}
        <div className="space-y-2">
          <Label className="text-sm text-muted-foreground uppercase tracking-wider">Introduction</Label>
          <Textarea
            value={letterData.intro_text}
            onChange={(e) => handleChange('intro_text', e.target.value)}
            onBlur={saveNow}
            rows={8}
            className="bg-background/50 font-mono text-sm"
          />
        </div>

        <Separator />

        {/* Production Schedule Section */}
        <div className="space-y-4">
          <Label className="text-sm text-muted-foreground uppercase tracking-wider">Production Schedule</Label>
          
          <div className="space-y-2">
            <Label>Room Availability</Label>
            <Textarea
              value={letterData.room_availability}
              onChange={(e) => handleChange('room_availability', e.target.value)}
              onBlur={saveNow}
              rows={2}
              className="bg-background/50"
            />
          </div>

          <div className="space-y-2">
            <Label>Loading Dock</Label>
            <Textarea
              value={letterData.loading_dock}
              onChange={(e) => handleChange('loading_dock', e.target.value)}
              onBlur={saveNow}
              rows={2}
              className="bg-background/50"
            />
          </div>

          <div className="space-y-2">
            <Label>Pre-Load-In Requirements</Label>
            <Textarea
              value={letterData.pre_load_in}
              onChange={(e) => handleChange('pre_load_in', e.target.value)}
              onBlur={saveNow}
              rows={2}
              className="bg-background/50"
            />
          </div>

          <div className="p-4 bg-background/30 rounded-lg">
            <p className="text-sm text-muted-foreground mb-2">Schedule items will be pulled automatically from the Schedule tab (Trucking, Setup, Strike, Rehearsal, Show types)</p>
          </div>

          <div className="space-y-2">
            <Label>Additional Schedule Notes</Label>
            <Textarea
              value={letterData.schedule_notes}
              onChange={(e) => handleChange('schedule_notes', e.target.value)}
              onBlur={saveNow}
              rows={2}
              placeholder="Any additional notes about the schedule..."
              className="bg-background/50"
            />
          </div>
        </div>

        <Separator />

        {/* Electrical Requirements */}
        <div className="space-y-2">
          <Label className="text-sm text-muted-foreground uppercase tracking-wider">Electrical Requirements</Label>
          <p className="text-xs text-muted-foreground">Auto-generated from Electric tab. Edit below if needed.</p>
          <Textarea
            value={letterData.electrical_text}
            onChange={(e) => handleChange('electrical_text', e.target.value)}
            onBlur={saveNow}
            rows={4}
            className="bg-background/50"
          />
        </div>

        <Separator />

        {/* Rigging & Lighting */}
        <div className="space-y-2">
          <Label className="text-sm text-muted-foreground uppercase tracking-wider">Rigging & Lighting</Label>
          <Textarea
            value={letterData.rigging_text}
            onChange={(e) => handleChange('rigging_text', e.target.value)}
            onBlur={saveNow}
            rows={2}
            className="bg-background/50"
          />
        </div>

        <Separator />

        {/* Venue Supplied Equipment */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-sm text-muted-foreground uppercase tracking-wider">Venue Supplied Equipment</Label>
            <Button type="button" variant="outline" size="sm" onClick={addEquipmentItem}>
              <Plus className="h-4 w-4 mr-1" /> Add Item
            </Button>
          </div>
          {letterData.venue_equipment.length === 0 ? (
            <p className="text-sm text-muted-foreground">No equipment items added yet.</p>
          ) : (
            <DndContext sensors={equipmentSensors} collisionDetection={closestCenter} onDragEnd={handleEquipmentDragEnd}>
              <SortableContext
                items={letterData.venue_equipment.map((item, i) => item.id || `eq-${i}`)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-2">
                  {letterData.venue_equipment.map((item, index) => {
                    const itemId = item.id || `eq-${index}`;
                    return (
                      <SortableEquipmentRow key={itemId} id={itemId}>
                        <Input
                          value={item.qty}
                          onChange={(e) => updateEquipmentItem(index, 'qty', e.target.value)}
                          onBlur={saveNow}
                          placeholder="Qty"
                          className="w-20 bg-background/50"
                        />
                        <Input
                          value={item.description}
                          onChange={(e) => updateEquipmentItem(index, 'description', e.target.value)}
                          onBlur={saveNow}
                          placeholder="Description (e.g., 8' x 24' x 32&quot; Main Stage with Stair Units)"
                          className="flex-1 bg-background/50"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => removeEquipmentItem(index)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </SortableEquipmentRow>
                    );
                  })}
                </div>
              </SortableContext>
            </DndContext>
          )}
        </div>

        <Separator />

        {/* Empty Case Storage */}
        <div className="space-y-2">
          <Label className="text-sm text-muted-foreground uppercase tracking-wider">Empty Case Storage</Label>
          <Input
            value={letterData.case_storage}
            onChange={(e) => handleChange('case_storage', e.target.value)}
            onBlur={saveNow}
            placeholder="e.g., Cases will be stored backstage in room 6E"
            className="bg-background/50"
          />
        </div>

        <Separator />

        {/* Closing */}
        <div className="space-y-2">
          <Label className="text-sm text-muted-foreground uppercase tracking-wider">Closing</Label>
          <Input
            value={letterData.closing_text}
            onChange={(e) => handleChange('closing_text', e.target.value)}
            onBlur={saveNow}
            className="bg-background/50"
          />
        </div>

        <Separator />

        {/* CC Recipients */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-sm text-muted-foreground uppercase tracking-wider">CC Recipients</Label>
            <Button type="button" variant="outline" size="sm" onClick={addCCContact}>
              <Plus className="h-4 w-4 mr-1" /> Add CC
            </Button>
          </div>
          {letterData.cc_contacts.length === 0 ? (
            <p className="text-sm text-muted-foreground">No CC recipients added.</p>
          ) : (
            <div className="space-y-2">
              {letterData.cc_contacts.map((cc, index) => (
                <div key={index} className="flex gap-2 items-start">
                  <Input
                    value={cc.name}
                    onChange={(e) => updateCCContact(index, 'name', e.target.value)}
                    onBlur={saveNow}
                    placeholder="Name"
                    className="flex-1 bg-background/50"
                  />
                  <Input
                    value={cc.organization}
                    onChange={(e) => updateCCContact(index, 'organization', e.target.value)}
                    onBlur={saveNow}
                    placeholder="Organization"
                    className="flex-1 bg-background/50"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeCCContact(index)}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default LogisticsLetterTab;
